import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Plus, GripVertical, Home, Users, X, Sparkles, Shuffle, Loader2, Check, RotateCcw, AlertTriangle, Edit, Trash2, Download, FileText } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';

const BUILDING_ORDER = ['CHAD', 'BO', 'Cabins'];
const csvCell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
const downloadCsv = (rows, filename) => {
  const blob = new Blob([rows.map(r => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv;charset=utf-8;' });
  const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: filename });
  a.click();
  URL.revokeObjectURL(a.href);
};
const byName = (a, b) => (a.last_name || '').localeCompare(b.last_name || '') || (a.first_name || '').localeCompare(b.first_name || '');
const byFirstName = (a, b) => (a.first_name || '').localeCompare(b.first_name || '') || (a.last_name || '').localeCompare(b.last_name || '');
const sortRooms = (rooms) => [...rooms].sort((a, b) => {
  const ia = BUILDING_ORDER.indexOf(a.building || ''), ib = BUILDING_ORDER.indexOf(b.building || '');
  return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
    || (a.building || '').localeCompare(b.building || '')
    || a.name.localeCompare(b.name, undefined, { numeric: true });
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`
});

// Draggable attendee chip
function DraggableAttendee({ attendee, isInRoom }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: attendee.id,
    data: { attendee, fromRoom: attendee.room_id },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-grab active:cursor-grabbing transition-all
        ${isDragging ? 'opacity-30' : 'opacity-100'}
        ${isInRoom
          ? 'bg-white border-gray-200 shadow-sm hover:shadow-md'
          : 'bg-white border-gray-200 hover:border-orange-300 hover:shadow-sm'
        }`}
      data-testid={`draggable-attendee-${attendee.id}`}
    >
      <GripVertical className="w-4 h-4 text-gray-400 shrink-0" />
      <div className="w-7 h-7 bg-gradient-to-br from-wine to-burgundy rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0">
        {attendee.first_name[0]}{attendee.last_name[0]}
      </div>
      <span className="text-sm font-medium text-gray-800 truncate flex-1">
        {attendee.first_name} {attendee.last_name}
      </span>
      {attendee.manual_assign_only && (
        <span className="text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 shrink-0">
          Manual
        </span>
      )}
    </div>
  );
}

// Droppable room card
function DroppableRoom({ room, assignedAttendees, onRemove, onEdit, onDelete }) {
  const { isOver, setNodeRef } = useDroppable({
    id: room.id,
    data: { room },
  });

  const spotsLeft = room.capacity - assignedAttendees.length;
  const isFull = spotsLeft <= 0;

  return (
    <div
      ref={setNodeRef}
      className={`rounded-xl border-2 transition-all min-h-[160px] group/room
        ${isOver && !isFull ? 'border-orange-400 bg-orange-50 shadow-lg scale-[1.01]' : ''}
        ${isOver && isFull ? 'border-red-400 bg-red-50' : ''}
        ${!isOver ? 'border-gray-200 bg-white hover:border-gray-300' : ''}
      `}
      data-testid={`droppable-room-${room.id}`}
    >
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Home className="w-5 h-5 text-gray-600 shrink-0" />
            <h3 className="font-bold text-gray-900 truncate">{room.name}</h3>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(room)}
              className="h-7 w-7 p-0 text-gray-400 hover:text-gray-700 opacity-0 group-hover/room:opacity-100 transition-opacity"
              title="Edit room"
              data-testid={`edit-room-${room.id}`}
            >
              <Edit className="w-3.5 h-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(room)}
              className="h-7 w-7 p-0 text-gray-400 hover:text-red-600 opacity-0 group-hover/room:opacity-100 transition-opacity"
              title="Delete room"
              data-testid={`delete-room-${room.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
            <Badge className={spotsLeft > 0 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}>
              {assignedAttendees.length}/{room.capacity}
            </Badge>
          </div>
        </div>
        <div className="flex gap-3 mt-1 text-xs text-gray-500">
          <span className="capitalize">{room.room_type}</span>
          {room.floor && <span>Floor {room.floor}</span>}
          {room.building && <span>{room.building}</span>}
        </div>
      </div>
      <div className="p-3 space-y-2">
        {assignedAttendees.length === 0 ? (
          <p className="text-sm text-gray-400 italic text-center py-4">
            Drop attendees here
          </p>
        ) : (
          assignedAttendees.map((a) => (
            <div key={a.id} className="flex items-center gap-1.5 group">
              <div className="flex-1 min-w-0">
                <DraggableAttendee attendee={a} isInRoom={true} />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemove(a.id)}
                className="text-gray-400 hover:text-red-500 shrink-0 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remove-assignment-${a.id}`}
                title="Remove from room"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Droppable unassigned pool
function UnassignedPool({ children }) {
  const { isOver, setNodeRef } = useDroppable({
    id: 'unassigned',
    data: { isUnassigned: true },
  });

  return (
    <div
      ref={setNodeRef}
      className={`space-y-2 min-h-[200px] p-3 rounded-xl border-2 border-dashed transition-all
        ${isOver ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-gray-50/50'}
      `}
      data-testid="unassigned-pool"
    >
      {children}
    </div>
  );
}

const RoomAssignment = () => {
  const [rooms, setRooms] = useState([]);
  const [roomsVisible, setRoomsVisible] = useState(false);

  const fetchVisibility = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/retreat/config`, { headers: getAuthHeaders() });
      setRoomsVisible(!!res.data.rooms_visible_to_attendees);
    } catch { /* default hidden */ }
  }, []);
  useEffect(() => { fetchVisibility(); }, [fetchVisibility]);

  const toggleVisibility = async () => {
    const next = !roomsVisible;
    if (!window.confirm(next
      ? 'Reveal room assignments to attendees? Everyone will see their room and roommates in their portal.'
      : 'Hide room assignments? Attendees will see "revealed at check-in" instead of their room.')) return;
    try {
      await axios.put(`${BACKEND_URL}/api/retreat/config`, { rooms_visible_to_attendees: next }, { headers: getAuthHeaders() });
      setRoomsVisible(next);
      toast.success(next ? 'Rooms are now VISIBLE to attendees' : 'Rooms are now HIDDEN from attendees');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update visibility');
    }
  };
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [deletingRoom, setDeletingRoom] = useState(null);
  const [roomForm, setRoomForm] = useState({
    name: '', room_type: 'shared', capacity: 2, beds: '', floor: '', building: '', notes: ''
  });
  const [savingRoom, setSavingRoom] = useState(false);
  const [showAutoAssign, setShowAutoAssign] = useState(false);
  const [autoAssignBusy, setAutoAssignBusy] = useState(false);
  const [autoAssignPreview, setAutoAssignPreview] = useState(null);
  const [groupSize, setGroupSize] = useState(6);
  const [enrolledIds, setEnrolledIds] = useState([]);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetDeleteRooms, setResetDeleteRooms] = useState(true);
  const [resetKeepManual, setResetKeepManual] = useState(true);
  const [pdfBusy, setPdfBusy] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const fetchData = useCallback(async () => {
    try {
      const [roomsRes, attendeesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/retreat/rooms`, { headers: getAuthHeaders() }),
        axios.get(`${BACKEND_URL}/api/retreat/attendees`, { headers: getAuthHeaders() }),
      ]);
      setRooms(roomsRes.data);
      setAttendees(attendeesRes.data);
    } catch {
      toast.error('Failed to load room data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const unassignedAttendees = attendees.filter(
    a => !a.room_id && a.ticket_status !== 'waitlist' && a.ticket_status !== 'cancelled'
  );

  const getAssignedAttendees = (roomId) =>
    attendees.filter(a => a.room_id === roomId);

  const activeAttendee = activeId ? attendees.find(a => a.id === activeId) : null;

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const attendeeId = active.id;
    const targetId = over.id;
    const attendee = attendees.find(a => a.id === attendeeId);
    if (!attendee) return;

    // Dropping back onto unassigned
    if (targetId === 'unassigned') {
      if (!attendee.room_id) return; // already unassigned
      try {
        await axios.delete(
          `${BACKEND_URL}/api/retreat/room-assignments/${attendeeId}`,
          { headers: getAuthHeaders() }
        );
        toast.success(`${attendee.first_name} unassigned`);
        fetchData();
      } catch {
        toast.error('Failed to unassign');
      }
      return;
    }

    // Dropping onto a room
    const room = rooms.find(r => r.id === targetId);
    if (!room) return;
    if (attendee.room_id === targetId) return; // same room

    const currentCount = getAssignedAttendees(targetId).length;
    if (currentCount >= room.capacity) {
      toast.error(`${room.name} is at full capacity`);
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/retreat/room-assignments`,
        { room_id: targetId, attendee_id: attendeeId },
        { headers: getAuthHeaders() }
      );
      toast.success(`${attendee.first_name} assigned to ${room.name}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign room');
    }
  };

  const handleRemoveAssignment = async (attendeeId) => {
    try {
      await axios.delete(
        `${BACKEND_URL}/api/retreat/room-assignments/${attendeeId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Assignment removed');
      fetchData();
    } catch {
      toast.error('Failed to remove assignment');
    }
  };

  const handleSaveRoom = async () => {
    if (!roomForm.name || !roomForm.capacity) {
      toast.error('Room name and capacity are required');
      return;
    }
    setSavingRoom(true);
    try {
      const beds = roomForm.beds
        ? (typeof roomForm.beds === 'string' ? roomForm.beds.split(',').map(b => b.trim()).filter(Boolean) : roomForm.beds)
        : [];
      const payload = { ...roomForm, capacity: parseInt(roomForm.capacity), beds };
      if (editingRoom) {
        await axios.put(
          `${BACKEND_URL}/api/retreat/rooms/${editingRoom.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Room updated');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/retreat/rooms`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Room created');
      }
      closeRoomModal();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save room');
    } finally {
      setSavingRoom(false);
    }
  };

  const openEditRoom = (room) => {
    setEditingRoom(room);
    setRoomForm({
      name: room.name || '',
      room_type: room.room_type || 'shared',
      capacity: room.capacity || 2,
      beds: Array.isArray(room.beds) ? room.beds.join(', ') : (room.beds || ''),
      floor: room.floor || '',
      building: room.building || '',
      notes: room.notes || '',
    });
    setShowRoomModal(true);
  };

  const closeRoomModal = () => {
    setShowRoomModal(false);
    setEditingRoom(null);
    setRoomForm({ name: '', room_type: 'shared', capacity: 2, beds: '', floor: '', building: '', notes: '' });
  };

  const handleDeleteRoom = async (force = false) => {
    if (!deletingRoom) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/retreat/rooms/${deletingRoom.id}${force ? '?force=true' : ''}`,
        { headers: getAuthHeaders() }
      );
      toast.success(`${deletingRoom.name} deleted`);
      setDeletingRoom(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete room');
    }
  };

  // ====== AUTO ASSIGN ======
  const openAutoAssign = () => {
    // Default-enroll every unassigned eligible attendee
    setEnrolledIds(unassignedAttendees.map(a => a.id));
    setAutoAssignPreview(null);
    setShowAutoAssign(true);
  };

  const toggleEnrolled = (id) => {
    setEnrolledIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    setAutoAssignPreview(null);
  };

  const generatePreview = async () => {
    if (enrolledIds.length === 0) {
      toast.error('Select at least one attendee to enroll');
      return;
    }
    setAutoAssignBusy(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/retreat/rooms/auto-assign/preview`,
        { attendee_ids: enrolledIds, group_size: groupSize, room_name_prefix: 'Cabin' },
        { headers: getAuthHeaders() }
      );
      setAutoAssignPreview(res.data);
      toast.success(`Generated ${res.data.group_count} groups — review and shuffle if needed`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate preview');
    } finally {
      setAutoAssignBusy(false);
    }
  };

  const commitAutoAssign = async () => {
    if (!autoAssignPreview) return;
    setAutoAssignBusy(true);
    try {
      const groups = autoAssignPreview.groups.map(g => ({
        room_name: g.room_name,
        room_id: g.room_id || null,
        attendee_ids: g.attendees.map(a => a.id),
      }));
      const res = await axios.post(
        `${BACKEND_URL}/api/retreat/rooms/auto-assign/commit`,
        { groups, room_type: 'shared' },
        { headers: getAuthHeaders() }
      );
      toast.success(`Filled ${res.data.rooms_filled} rooms · created ${res.data.rooms_created} cabins · placed ${res.data.assignments_created} men`);
      setShowAutoAssign(false);
      setAutoAssignPreview(null);
      setEnrolledIds([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to commit assignments');
    } finally {
      setAutoAssignBusy(false);
    }
  };

  const handleReset = async () => {
    setResetBusy(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/retreat/room-assignments/reset`,
        { delete_rooms: resetDeleteRooms, keep_manual_only: resetKeepManual },
        { headers: getAuthHeaders() }
      );
      const parts = [
        `${res.data.attendees_reset} attendees unassigned`,
        res.data.rooms_deleted > 0 ? `${res.data.rooms_deleted} rooms removed` : null,
      ].filter(Boolean);
      toast.success(parts.join(' · '));
      setShowResetModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    } finally {
      setResetBusy(false);
    }
  };

  // ====== EXPORT ======
  const exportByRoom = () => {
    const rows = [['Building', 'Room', 'Capacity', 'Occupied', 'Attendee', 'Email', 'Phone']];
    sortRooms(rooms).forEach(room => {
      const occ = getAssignedAttendees(room.id).sort(byName);
      if (occ.length === 0) rows.push([room.building || '', room.name, room.capacity, 0, '(empty)', '', '']);
      occ.forEach(a => rows.push([room.building || '', room.name, room.capacity, occ.length, `${a.last_name}, ${a.first_name}`, a.email || '', a.phone || '']));
    });
    if (unassignedAttendees.length) {
      unassignedAttendees
        .sort(byName)
        .forEach(a => rows.push(['', 'UNASSIGNED', '', '', `${a.last_name}, ${a.first_name}`, a.email || '', a.phone || '']));
    }
    downloadCsv(rows, 'room-assignments-by-room.csv');
    toast.success('Exported room layout (by room)');
  };

  const exportAlphabetical = (byFirst = false) => {
    const roomById = Object.fromEntries(rooms.map(r => [r.id, r]));
    const rows = [byFirst
      ? ['First Name', 'Last Name', 'Building', 'Room', 'Roommates', 'Email', 'Phone']
      : ['Last Name', 'First Name', 'Building', 'Room', 'Roommates', 'Email', 'Phone']];
    attendees
      .filter(a => a.ticket_status !== 'waitlist' && a.ticket_status !== 'cancelled')
      .sort(byFirst ? byFirstName : byName)
      .forEach(a => {
        const room = a.room_id ? roomById[a.room_id] : null;
        const mates = room ? getAssignedAttendees(room.id).filter(m => m.id !== a.id).map(m => `${m.first_name} ${m.last_name}`).join('; ') : '';
        const names = byFirst ? [a.first_name, a.last_name] : [a.last_name, a.first_name];
        rows.push([...names, room?.building || '', room ? room.name : 'UNASSIGNED', mates, a.email || '', a.phone || '']);
      });
    downloadCsv(rows, byFirst ? 'room-assignments-by-first-name.csv' : 'room-assignments-alphabetical.csv');
    toast.success(byFirst ? 'Exported room assignments (by first name)' : 'Exported room assignments (A–Z)');
  };

  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/retreat/rooms/export/pdf`, { headers: getAuthHeaders(), responseType: 'blob' });
      const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(res.data), download: 'room-assignments.pdf' });
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('Room signs PDF downloaded');
    } catch {
      toast.error('Failed to generate PDF');
    } finally {
      setPdfBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="room-assignment-page">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Room Assignments</h1>
          <p className="text-gray-600 mt-1">
            Drag attendees to assign rooms &middot; {unassignedAttendees.length} unassigned
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-rooms-btn" disabled={pdfBusy}>
                {pdfBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportPdf} data-testid="export-room-signs-pdf">
                <FileText className="w-4 h-4 mr-2" />Room Signs (PDF, 1 per room)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportByRoom} data-testid="export-by-room">
                <Home className="w-4 h-4 mr-2" />By Room (CSV)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportAlphabetical(false)} data-testid="export-alphabetical">
                <Users className="w-4 h-4 mr-2" />By Last Name A–Z (CSV)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportAlphabetical(true)} data-testid="export-by-first-name">
                <Users className="w-4 h-4 mr-2" />By First Name A–Z (CSV)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            onClick={toggleVisibility}
            variant="outline"
            className={roomsVisible
              ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
              : 'border-amber-300 text-amber-700 hover:bg-amber-50'}
            data-testid="rooms-visibility-toggle"
          >
            {roomsVisible ? 'Visible to attendees' : 'Hidden from attendees'}
          </Button>
          <Button
            onClick={() => setShowResetModal(true)}
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-50"
            data-testid="reset-assignments-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />Reset
          </Button>
          <Button
            onClick={openAutoAssign}
            disabled={unassignedAttendees.length === 0}
            variant="outline"
            className="border-orange-300 text-orange-700 hover:bg-orange-50"
            data-testid="auto-assign-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />Auto Assign
          </Button>
          <Button
            onClick={() => setShowRoomModal(true)}
            className="bg-gradient-to-r from-wine to-burgundy hover:from-wine-600 hover:to-burgundy-600"
            data-testid="add-room-btn"
          >
            <Plus className="w-4 h-4 mr-2" />Add Room
          </Button>
        </div>
      </div>

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Unassigned Attendees */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Unassigned ({unassignedAttendees.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <UnassignedPool>
                  {unassignedAttendees.length === 0 ? (
                    <p className="text-sm text-gray-400 italic text-center py-4">
                      All attendees are assigned
                    </p>
                  ) : (
                    unassignedAttendees.map((a) => (
                      <DraggableAttendee key={a.id} attendee={a} isInRoom={false} />
                    ))
                  )}
                </UnassignedPool>
              </CardContent>
            </Card>
          </div>

          {/* Room Grid */}
          <div className="lg:col-span-3">
            {rooms.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <Home className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No rooms yet</p>
                  <p className="text-sm mt-1">Add rooms to start assigning attendees</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {(() => {
                  const order = ['CHAD', 'BO', 'Cabins'];
                  const groups = {};
                  rooms.forEach(r => {
                    const b = r.building || 'Unassigned Area';
                    (groups[b] = groups[b] || []).push(r);
                  });
                  const keys = Object.keys(groups).sort((a, b) => {
                    const ia = order.indexOf(a), ib = order.indexOf(b);
                    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
                  });
                  const natural = (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true });
                  return keys.map(building => (
                    <div key={building} data-testid={`room-group-${building.toLowerCase().replace(/\s+/g, '-')}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">{building}</h3>
                        <span className="text-xs text-gray-400">
                          {groups[building].length} room{groups[building].length === 1 ? '' : 's'} ·{' '}
                          {groups[building].reduce((s, r) => s + r.capacity, 0)} beds
                        </span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {groups[building].sort(natural).map((room) => (
                          <DroppableRoom
                            key={room.id}
                            room={room}
                            assignedAttendees={getAssignedAttendees(room.id)}
                            onRemove={handleRemoveAssignment}
                            onEdit={openEditRoom}
                            onDelete={(r) => setDeletingRoom(r)}
                          />
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Drag overlay for visual feedback */}
        <DragOverlay>
          {activeAttendee ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-orange-400 bg-white shadow-xl cursor-grabbing">
              <GripVertical className="w-4 h-4 text-orange-400" />
              <div className="w-7 h-7 bg-gradient-to-br from-wine to-burgundy rounded-full flex items-center justify-center text-white text-xs font-semibold">
                {activeAttendee.first_name[0]}{activeAttendee.last_name[0]}
              </div>
              <span className="text-sm font-medium">{activeAttendee.first_name} {activeAttendee.last_name}</span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Reset Confirmation Modal */}
      <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
        <DialogContent data-testid="reset-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Reset Room Assignments?
            </DialogTitle>
            <DialogDescription>
              Move everyone back to the unassigned pool so you can re-run auto-assign or rebuild rooms from scratch.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-900">
              <p className="font-semibold mb-1">This will:</p>
              <ul className="list-disc list-inside text-xs space-y-0.5 text-red-800">
                <li>Unassign all {attendees.filter(a => a.room_id && !(resetKeepManual && a.manual_assign_only)).length} currently-placed attendees</li>
                {resetDeleteRooms && <li>Delete all empty rooms after clearing</li>}
                {resetKeepManual && <li>Leave manual-only attendees in their current rooms</li>}
              </ul>
            </div>

            <label className="flex items-start gap-2.5 p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={resetKeepManual}
                onChange={(e) => setResetKeepManual(e.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-orange-600"
                data-testid="reset-keep-manual"
              />
              <div className="text-xs">
                <p className="font-semibold text-gray-800">Keep manual-only attendees in their rooms</p>
                <p className="text-gray-500 mt-0.5">Recommended — preserves your handpicked staff/VIP placements.</p>
              </div>
            </label>

            <label className="flex items-start gap-2.5 p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                checked={resetDeleteRooms}
                onChange={(e) => setResetDeleteRooms(e.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-orange-600"
                data-testid="reset-delete-rooms"
              />
              <div className="text-xs">
                <p className="font-semibold text-gray-800">Also delete empty rooms</p>
                <p className="text-gray-500 mt-0.5">Removes auto-generated cabins (e.g. "Cabin A", "Cabin B") that end up empty after reset.</p>
              </div>
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetModal(false)}>Cancel</Button>
            <Button
              onClick={handleReset}
              disabled={resetBusy}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-reset-btn"
            >
              {resetBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              Reset Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto Assign Modal */}
      <Dialog open={showAutoAssign} onOpenChange={setShowAutoAssign}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="auto-assign-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-orange-500" />
              Auto-Generate Room Groups
            </DialogTitle>
            <DialogDescription>
              Randomly fills your empty rooms first (CHAD, then BO), then creates new Cabins for the overflow. Rooms that already have someone are left alone. Nothing is saved until you click <strong>Confirm</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {/* Step 1: Enrollment */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Step 1 · Enroll attendees</Label>
                <div className="flex gap-2">
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => { setEnrolledIds(unassignedAttendees.map(a => a.id)); setAutoAssignPreview(null); }}
                    className="text-xs"
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => { setEnrolledIds([]); setAutoAssignPreview(null); }}
                    className="text-xs"
                  >
                    Clear
                  </Button>
                </div>
              </div>
              <div className="max-h-56 overflow-y-auto border rounded-lg p-2 space-y-1 bg-gray-50">
                {unassignedAttendees.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-3">No unassigned attendees</p>
                ) : unassignedAttendees.map(a => {
                  const enrolled = enrolledIds.includes(a.id);
                  return (
                    <label
                      key={a.id}
                      className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded transition-colors text-sm cursor-pointer
                        ${enrolled ? 'bg-orange-50 border border-orange-200' : 'hover:bg-white border border-transparent'}`}
                      data-testid={`enroll-${a.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={enrolled}
                        onChange={() => toggleEnrolled(a.id)}
                        className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                      />
                      <span className="font-medium text-gray-800">{a.first_name} {a.last_name}</span>
                      {a.room_preference && (
                        <span className="text-xs text-gray-500 capitalize ml-auto truncate max-w-[200px]">{a.room_preference}</span>
                      )}
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-1.5">
                {enrolledIds.length} enrolled of {unassignedAttendees.length} unassigned
              </p>
            </div>

            {/* Step 2: Group size */}
            <div className="flex items-end gap-3">
              <div className="space-y-1.5 flex-1">
                <Label className="text-sm font-semibold">Step 2 · Cabin size (overflow only)</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex rounded-md overflow-hidden border border-gray-300">
                    {[6, 8, 9].map(size => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => { setGroupSize(size); setAutoAssignPreview(null); }}
                        className={`px-4 py-1.5 text-sm font-semibold transition-colors ${
                          groupSize === size
                            ? 'bg-gradient-to-r from-wine to-burgundy text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                        data-testid={`group-size-preset-${size}`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">or</span>
                  <Input
                    type="number"
                    min={1} max={12}
                    value={groupSize}
                    onChange={(e) => { setGroupSize(parseInt(e.target.value) || 6); setAutoAssignPreview(null); }}
                    className="w-20"
                    data-testid="group-size-input"
                  />
                  <span className="text-sm text-gray-600">per cabin</span>
                </div>
              </div>
              <Button
                onClick={generatePreview}
                disabled={autoAssignBusy || enrolledIds.length === 0}
                className="bg-gradient-to-r from-wine to-burgundy hover:from-wine-600 hover:to-burgundy-600"
                data-testid="generate-preview-btn"
              >
                {autoAssignBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shuffle className="w-4 h-4 mr-2" />}
                {autoAssignPreview ? 'Re-shuffle' : 'Generate Preview'}
              </Button>
            </div>

            {/* Step 3: Preview */}
            {autoAssignPreview && (
              <div data-testid="auto-assign-preview">
                <Label className="text-sm font-semibold">Step 3 · Preview</Label>
                <p className="text-xs text-gray-500 mt-0.5 mb-3">
                  {autoAssignPreview.filled_existing_count} existing room{autoAssignPreview.filled_existing_count !== 1 ? 's' : ''} filled
                  {' · '}{autoAssignPreview.new_cabin_count} new cabin{autoAssignPreview.new_cabin_count !== 1 ? 's' : ''}
                  {' · '}{autoAssignPreview.total_attendees} men placed
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-72 overflow-y-auto">
                  {autoAssignPreview.groups.map((group, i) => (
                    <div
                      key={i}
                      className={`border rounded-lg p-3 ${group.is_new ? 'border-emerald-300 bg-emerald-50/40' : 'border-gray-200 bg-white'}`}
                      data-testid={`preview-group-${i}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-sm text-gray-900">{group.room_name}</h4>
                          {group.is_new ? (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 uppercase tracking-wide">New</span>
                          ) : group.building && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 uppercase tracking-wide">{group.building}</span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">{group.size}{!group.is_new && group.capacity ? `/${group.capacity}` : ''} {group.size === 1 && group.is_new ? 'person' : ''}</span>
                      </div>
                      <div className="space-y-1">
                        {group.attendees.map(a => (
                          <div key={a.id} className="flex items-center gap-2 text-xs">
                            <div className="w-5 h-5 bg-gradient-to-br from-wine to-burgundy rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                              {a.first_name[0]}{a.last_name[0]}
                            </div>
                            <span className="text-gray-700 truncate">{a.first_name} {a.last_name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAutoAssign(false)}>Cancel</Button>
            <Button
              onClick={commitAutoAssign}
              disabled={autoAssignBusy || !autoAssignPreview}
              className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
              data-testid="commit-auto-assign-btn"
            >
              {autoAssignBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Confirm & Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Room Modal */}
      <Dialog open={showRoomModal} onOpenChange={(open) => { if (!open) closeRoomModal(); }}>
        <DialogContent data-testid="add-room-modal">
          <DialogHeader>
            <DialogTitle>{editingRoom ? `Edit ${editingRoom.name}` : 'Add Room'}</DialogTitle>
            <DialogDescription>
              {editingRoom ? 'Update room details. Changes apply immediately.' : 'Create a new room for the retreat.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Room Name *</Label>
              <Input
                value={roomForm.name}
                onChange={(e) => setRoomForm({ ...roomForm, name: e.target.value })}
                placeholder="e.g., Cabin A"
                data-testid="room-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Room Type</Label>
                <Select value={roomForm.room_type} onValueChange={(v) => setRoomForm({ ...roomForm, room_type: v })}>
                  <SelectTrigger data-testid="room-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="shared">Shared</SelectItem>
                    <SelectItem value="double">Double</SelectItem>
                    <SelectItem value="single">Single</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Capacity *</Label>
                <Select value={String(roomForm.capacity)} onValueChange={(v) => setRoomForm({ ...roomForm, capacity: v })}>
                  <SelectTrigger data-testid="room-capacity-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                      <SelectItem key={n} value={String(n)}>{n} {n === 1 ? 'person' : 'people'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Bed Labels (comma-separated)</Label>
              <Input
                value={roomForm.beds}
                onChange={(e) => setRoomForm({ ...roomForm, beds: e.target.value })}
                placeholder="e.g., Bed A, Bed B"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Floor</Label>
                <Input
                  value={roomForm.floor}
                  onChange={(e) => setRoomForm({ ...roomForm, floor: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Building / Area</Label>
                <Select value={roomForm.building || 'none'} onValueChange={(v) => setRoomForm({ ...roomForm, building: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="room-building-select"><SelectValue placeholder="Select area" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    <SelectItem value="CHAD">CHAD</SelectItem>
                    <SelectItem value="BO">BO</SelectItem>
                    <SelectItem value="Cabins">Cabins</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Input
                value={roomForm.notes}
                onChange={(e) => setRoomForm({ ...roomForm, notes: e.target.value })}
                placeholder="e.g., Connects with Room 5"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeRoomModal}>Cancel</Button>
            <Button
              onClick={handleSaveRoom}
              disabled={savingRoom}
              className="bg-gradient-to-r from-wine to-burgundy hover:from-wine-600 hover:to-burgundy-600"
              data-testid="save-room-btn"
            >
              {savingRoom
                ? (editingRoom ? 'Saving...' : 'Creating...')
                : (editingRoom ? 'Save Changes' : 'Create Room')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Room Confirmation */}
      <Dialog open={!!deletingRoom} onOpenChange={(open) => { if (!open) setDeletingRoom(null); }}>
        <DialogContent data-testid="delete-room-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete {deletingRoom?.name}?
            </DialogTitle>
            <DialogDescription>
              {deletingRoom && getAssignedAttendees(deletingRoom.id).length > 0 ? (
                <>
                  This room has <strong>{getAssignedAttendees(deletingRoom.id).length}</strong> attendee
                  {getAssignedAttendees(deletingRoom.id).length === 1 ? '' : 's'} currently assigned.
                  Deleting will move them back to the unassigned pool.
                </>
              ) : (
                "This room is empty and will be permanently removed."
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingRoom(null)}>Cancel</Button>
            <Button
              onClick={() => handleDeleteRoom(deletingRoom && getAssignedAttendees(deletingRoom.id).length > 0)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-room-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {deletingRoom && getAssignedAttendees(deletingRoom.id).length > 0 ? 'Unassign & Delete' : 'Delete Room'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoomAssignment;
